import { AssociateType } from '../../components/Associate/enum';
import { ShipmentHistoryUtils } from '../../components/ShipmentHistory/utils/ShipmentHistoryUtils';
import { buildFetchShipmentDetailsApiUrl } from '../../pages/LoadBoard/action';
import {
    ILoadBoardSearchFilters,
    ILoadBoardStatusFilters,
} from '../../pages/LoadBoard/interfaces';
import { API_CONSTANTS } from '../../services/apiConstants';
import { IGenericApiResponse } from '../../utils/interfaces';
import { Routes } from '../../utils/routes';
import { apiCall } from '../axios';
import { IDriverCard } from '../driver/interfaces';
import { IDriverLoad } from '../loadBoard/interfaces';
import { IAttachmentResponse } from '../interfaces';
import { ISelectedDriverIdList } from '../../pages/LoadBoard/interfaces/ISelectedDriverList';
import {
    IAddDriverLoadLoadBoard,
    IPindrop,
    IShipment,
    IShipmentLoadBoardViewItem,
    IShipmentPayload,
} from './interfaces';
import { IOrderResponseModel } from '../mcs/interfaces';
import {
    IInvoicePreview,
    IMultipleInvoicePreview,
} from './interfaces/IInvoicePreview';
import { createInvoiceFormData } from '../../pages/LoadBoard/components/ShipmentDetails/components/Invoice/utils/invoiceCommonUtils';
import {
    IRecipientList,
    IInvoiceAttachmentOptions,
} from '../../pages/LoadBoard/components/ShipmentDetails/components/Invoice/interfaces/IRecipientList';
import { dispatchManagerFeatures } from '../../featureFlags/dispatchManagerFeatures';

const updateShipmentStatus = async (
    shipmentId: number,
    selectedDriverIdList: ISelectedDriverIdList[],
): Promise<{ status: number; error: boolean }> => {
    const response = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}${API_CONSTANTS.dispatch}/${shipmentId}`,
        selectedDriverIdList,
        true,
    );
    return response;
};

const updateShipment = async (
    shipmentId: number,
    payload: IShipmentPayload,
): Promise<void> => {
    await apiCall(
        'put',
        `${API_CONSTANTS.shipments}/${shipmentId}`,
        payload,
        true,
    );
};

const addDriverLoad = async (
    shipmentId: number,
    load: IDriverCard,
    numberOfTrucks = 1,
): Promise<number[]> => {
    return await apiCall(
        'post',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.loads}`,
        { ...load, numberOfTrucks },
        true,
    );
};

const addDriverLoadLoadBoard = async (
    shipmentId: number,
    load: IAddDriverLoadLoadBoard,
    numberOfTrucks = 1,
): Promise<number[]> => {
    return await apiCall(
        'post',
        `${shipmentId}${API_CONSTANTS.driverloads}${API_CONSTANTS.bycargoDetails}`,
        { ...load, numberOfTrucks },
        true,
    );
};

export const fetchFullShipmentDetails = async (
    shipmentId: number | string,
    signal?: AbortSignal,
): Promise<IShipment> => {
    const response = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}/${shipmentId}?include=Attachments, Associate, Associate.Contacts, Associate.Address, ShipmentOrders, ShipmentOrders.Order, ShipmentOrders.Order.Associate, ShipmentOrders.Order.Associate.Contacts, ShipmentOrders.Order.Associate.Address, Commodity, ShipmentLocations, ShipmentLocations.Location, ShipmentLocations.Location.TagGroup, ShipmentLocations.PinDrop, DriverLoads, DriverLoads.Associate, DriverLoads.Associate.Unavailabilities, DriverLoads.Associate.Contacts, DriverLoads.DriverLoadCharges, DriverLoads.Attachments, ShipmentStatus, ShipmentCargoDetails.Commodity, ShipmentCargoDetails, ShipmentCargoDetails.ShipmentCargoLocations, DriverLoads.DriverLoadCargoDetails, DriverLoads.DriverLoadCargoDetails.Commodity, DriverLoads.DriverLoadCargoDetails.DriverLoadCargoLocations, DriverLoads.DriverLoadBrokerFees`,
        {},
        true,
        false,
        false,
        'application/json',
        undefined,
        signal,
    );

    return response.data;
};

export const fetchShipmentCoreDetails = async (
    shipmentId: number | string,
    signal?: AbortSignal,
): Promise<IShipment> => {
    const response = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}/${shipmentId}?include=Attachments, Associate, Associate.Contacts, Associate.Address, ShipmentOrders, ShipmentOrders.Order, ShipmentOrders.Order.Associate, ShipmentOrders.Order.Associate.Contacts, ShipmentOrders.Order.Associate.Address, Commodity, ShipmentLocations, ShipmentLocations.Location, ShipmentLocations.Location.TagGroup, ShipmentLocations.PinDrop, ShipmentStatus, ShipmentCargoDetails.Commodity, ShipmentCargoDetails, ShipmentCargoDetails.ShipmentCargoLocations, Driverloads, TrailerRequirements, TrailerRequirements.TrailerSize`,
        {},
        true,
        false,
        false,
        'application/json',
        undefined,
        signal,
    );

    return response.data;
};

export const fetchShipmentDriverLoads = async (
    shipmentId: number | string,
    signal?: AbortSignal,
): Promise<IDriverLoad[]> => {
    const driverLoadInclude =
        'Associate, Associate.Unavailabilities, Associate.Contacts, DriverLoadCharges, Attachments, DriverLoadCargoDetails, DriverLoadCargoDetails.Commodity, DriverLoadCargoDetails.Customer, DriverLoadCargoDetails.DriverLoadCargoLocations, DriverLoadBrokerFees';
    const params = new URLSearchParams({
        include: driverLoadInclude,
        filter: `ShipmentId eq ${shipmentId}`,
    });
    const response: IGenericApiResponse<IDriverLoad[]> = await apiCall(
        'get',
        `${API_CONSTANTS.loads}?${params.toString()}`,
        {},
        true,
        false,
        false,
        'application/json',
        undefined,
        signal,
    );

    // Some backends may return an object wrapper instead of a bare array;
    // normalize to a plain array so callers always see IDriverLoad[].
    const data = response.data as unknown;
    return Array.isArray(data) ? (data as IDriverLoad[]) : [];
};

export const fetchFullShipmentDetailsV1 = async (
    shipmentId: number | string,
    signal?: AbortSignal,
): Promise<IShipment> => {
    const [shipmentCore, driverLoadsRaw] = await Promise.all([
        fetchShipmentCoreDetails(shipmentId, signal),
        fetchShipmentDriverLoads(shipmentId, signal),
    ]);

    const driverLoads = Array.isArray(driverLoadsRaw) ? driverLoadsRaw : [];

    const fullShipment: IShipment = {
        ...shipmentCore,
        driverLoads,
    };

    return fullShipment;
};

export const fetchFullOrderDetails = async (
    orderId: number | string,
    signal?: AbortSignal,
): Promise<IOrderResponseModel> => {
    const response = await apiCall(
        'get',
        `${API_CONSTANTS.mcs}/orders/${orderId}`,
        {},
        true,
        false,
        false,
        'application/json',
        undefined,
        signal,
    );

    return response.data;
};

const fetchShipmentDetails = async (
    loadBoardSearchFilters: ILoadBoardSearchFilters,
    loadBoardBadgesFilters: ILoadBoardStatusFilters,
    isInclude = true,
): Promise<IShipmentLoadBoardViewItem[]> => {
    const apiUrl: string = buildFetchShipmentDetailsApiUrl(
        loadBoardSearchFilters,
        loadBoardBadgesFilters,
        isInclude,
    );

    const response: IGenericApiResponse<IShipmentLoadBoardViewItem[]> =
        await apiCall('get', apiUrl, {}, true);

    return response.data;
};

export const fetchFullShipmentHistory = async (
    associateId: string | number,
    associateType: AssociateType,
    deliveryStartDate?: string | Date,
    deliveryEndDate?: string | Date,
): Promise<IShipment[]> => {
    const shipmentHistoryUtils = new ShipmentHistoryUtils();
    let url = `${API_CONSTANTS.shipments}/odata?`;

    url += shipmentHistoryUtils.shipmentHistoryFilterQuery(
        associateId,
        associateType,
        deliveryStartDate,
        deliveryEndDate,
    );

    const response: IGenericApiResponse<IShipment[]> = await apiCall(
        'get',
        url,
        {},
        true,
    );

    return response.data;
};

const fetchPinDropsSuggestions = async (
    suggestion: string,
): Promise<IPindrop[]> => {
    const response: IGenericApiResponse<IPindrop[]> = await apiCall(
        'get',
        `${API_CONSTANTS.pindrops}?include=location&sort=name&filter=name like ${suggestion}`,
        {},
        true,
    );
    return response.data;
};

const fetchAssociateShipmentDetail = async (
    associateId: number,
    associateRouteType: string,
): Promise<IShipment[]> => {
    let url = `${API_CONSTANTS.shipments}/odata?include=ShipmentLocations,ShipmentLocations.Location,ShipmentLocations.Location.TagGroup,Associate,ShipmentOrders,ShipmentOrders.Order,ShipmentOrders.Order.Associate,ShipmentStatus,DriverLoads,DriverLoads.Associate`;
    let filterString = `IsActive eq true`;

    if (associateRouteType === Routes.drivers) {
        filterString += ` and (DriverLoads/any(d: d/Associate/AssociateId eq ${associateId})) and ((ShipmentStatus/Name eq 'NeedsDrivers') or (ShipmentStatus/Name eq 'Ready') or (ShipmentStatus/Name eq 'Dispatched') or (ShipmentStatus/Name eq 'AwaitingConfirmation'))`;
    } else {
        const { showPopupWhileDeactivatingCustomer } = dispatchManagerFeatures;
        if (showPopupWhileDeactivatingCustomer) {
            filterString += ` and (Associate/AssociateId eq ${associateId}) and ((ShipmentStatus/Name eq 'NeedsDrivers') or (ShipmentStatus/Name eq 'Ready') or (ShipmentStatus/Name eq 'Dispatched') or (ShipmentStatus/Name eq 'AwaitingConfirmation') or (ShipmentStatus/Name eq 'ContinueBuilding') or (ShipmentStatus/Name eq 'Delivered') or (ShipmentStatus/Name eq 'ReadyForInvoicing'))`;
        } else {
            filterString += ` and (Associate/AssociateId eq ${associateId}) and ((ShipmentStatus/Name eq 'NeedsDrivers') or (ShipmentStatus/Name eq 'Ready') or (ShipmentStatus/Name eq 'Dispatched') or (ShipmentStatus/Name eq 'AwaitingConfirmation'))`;
        }
    }

    url += `&$filter=${filterString}`;

    const response: IGenericApiResponse<IShipment[]> = await apiCall(
        'get',
        url,
        {},
        true,
    );
    return response.data;
};

export const fetchShipmentAttachment = async (
    shipmentId: number,
    attachment: IAttachmentResponse,
): Promise<IGenericApiResponse<Blob>> => {
    const response: IGenericApiResponse<Blob> = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}/${shipmentId}${API_CONSTANTS.attachments}/${attachment.attachmentId}/${attachment.accessToken}`,
        {},
        true,
        true,
    );

    return response;
};

const fetchAssociateBreakdownDriverShipmentDetail = async (
    associateId: number,
): Promise<IShipment[]> => {
    const baseIncludeQuery = `ShipmentLocations,ShipmentLocations.Location,ShipmentLocations.Location.TagGroup,Associate,ShipmentOrders,ShipmentOrders.Order,ShipmentOrders.Order.Associate,ShipmentStatus,DriverLoads,DriverLoads.Associate`;
    const baseFilter = `IsActive eq true and (DriverLoads/any(driverLoad: driverLoad/AssociateId eq ${associateId} and driverLoad/status eq 'Dispatched'))`;

    const url = `${
        API_CONSTANTS.shipments
    }/odata?include=${baseIncludeQuery}&$filter=${encodeURIComponent(
        baseFilter,
    )}`;

    const response: IGenericApiResponse<IShipment[]> = await apiCall(
        'get',
        url,
        {},
        true,
    );
    return response.data;
};

const fetchShimpentInvoicePreviewDetails = async (
    shipmentId: number,
): Promise<IInvoicePreview> => {
    const response: IGenericApiResponse<IInvoicePreview> = await apiCall(
        'get',
        `${API_CONSTANTS.shipments}${API_CONSTANTS.invoicePreview}/${shipmentId}`,
        {},
        true,
    );
    return response.data;
};

const fetchMultipleShipmentsInvoicePreviewDetails = async (
    shipmentIds: number[],
    customerId?: number,
): Promise<IInvoicePreview[]> => {
    const payload = {
        shipmentIds,
        ...(customerId && { customerId }),
    };

    const response: IGenericApiResponse<IMultipleInvoicePreview> =
        await apiCall(
            'post',
            `${API_CONSTANTS.invoices}${API_CONSTANTS.multipleShipmentsPreview}`,
            payload,
            true,
        );

    const flattenedPreviews: IInvoicePreview[] = [];

    response.data.shipmentPreviews.forEach(shipmentPreview => {
        shipmentPreview.customerInvoices.forEach(customerInvoice => {
            if (customerId && customerInvoice.customerId !== customerId) {
                return;
            }

            flattenedPreviews.push({
                shipmentId: shipmentPreview.shipmentId,
                txnDate: shipmentPreview.txnDate,
                dueDate: shipmentPreview.dueDate,
                customerRef: customerInvoice.customerRef,
                templateRef: customerInvoice.templateRef,
                termsRef: customerInvoice.termsRef,
                lumpSumInvoiceNumber: customerInvoice.lumpSumInvoiceNumber,
                lumpSumTotalAmount: customerInvoice.lumpSumTotalAmount,
                driverLoadInvoiceDetails:
                    customerInvoice.driverLoadInvoiceDetails,
                methodOfInvoice: customerInvoice.methodOfInvoice,
                customerContacts: customerInvoice.customerContacts,
            });
        });
    });

    return flattenedPreviews;
};

const handleGenerateInvoices = async (
    customerId: number,
    shipmentIds: number[],
    recipients: IRecipientList,
    attachmentOptions: IInvoiceAttachmentOptions,
): Promise<IGenericApiResponse<{ Message?: string }>> => {
    const formData = createInvoiceFormData(
        customerId,
        shipmentIds,
        recipients,
        attachmentOptions,
    );

    const response = await apiCall(
        'post',
        `${API_CONSTANTS.invoices}/generate`,
        formData,
        true,
        false,
        true,
    );

    return response;
};

export const sendMultipleCustomerInvoices = async (
    formData: FormData,
): Promise<IGenericApiResponse<unknown>> => {
    const response = await apiCall(
        'post',
        `${API_CONSTANTS.shipments}${API_CONSTANTS.invoice}`,
        formData,
        true,
        false,
        true,
    );

    return response;
};

export const shipmentsApi = {
    updateShipmentStatus,
    fetchShipmentDetails,
    addDriverLoad,
    fetchFullShipmentDetails,
    fetchShipmentCoreDetails,
    fetchShipmentDriverLoads,
    fetchFullShipmentDetailsV1,
    fetchFullOrderDetails,
    updateShipment,
    fetchFullShipmentHistory,
    fetchPinDropsSuggestions,
    fetchAssociateShipmentDetail,
    fetchShipmentAttachment,
    fetchAssociateBreakdownDriverShipmentDetail,
    fetchShimpentInvoicePreviewDetails,
    addDriverLoadLoadBoard,
    fetchMultipleShipmentsInvoicePreviewDetails,
    handleGenerateInvoices,
    sendMultipleCustomerInvoices,
};
